import React, { useId } from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import { CheckBoxProps } from '../../types';

export const CheckBox: React.FC<CheckBoxProps> = (props) => {
  const {
    name,
    isSelected,
    onChange,
    onBlur,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    className,
    checkboxClassName,
    labelClassName,
    hintClassName,
    children
  } = props;

  const checkboxId = useId();
  const hasError = Boolean(error);
  const showHelperText = !hasError && helperText;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isDisabled && onChange) {
        // Create a synthetic event for onChange
        const syntheticEvent = {
          target: { checked: !isSelected },
          currentTarget: { checked: !isSelected }
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
  };

  return (
    <div className={cn(DEFAULT_STYLES.field, className, 'base__checkbox')}>
      <label
        htmlFor={checkboxId}
        className={cn(
          DEFAULT_STYLES.checkbox,
          isDisabled && DEFAULT_STYLES.checkboxDisabled,
          checkboxClassName
        )}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
      >
        <input
          type="checkbox"
          id={checkboxId}
          name={name}
          checked={isSelected}
          onChange={onChange}
          onBlur={onBlur}
          disabled={isDisabled}
          className={DEFAULT_STYLES.checkbox__input}
          aria-describedby={hasError ? `${checkboxId}-error` : undefined}
          aria-invalid={hasError}
        />

        {/* Custom Checkbox Visual - 16px total (border-box), 8px inner checkmark */}
        <span
          className={cn(
            DEFAULT_STYLES.checkbox__visual,
            // Unchecked states
            !isSelected &&
              !isDisabled &&
              DEFAULT_STYLES.checkbox__visualUnchecked,
            // Checked states
            isSelected && !isDisabled && DEFAULT_STYLES.checkbox__visualChecked,
            // Disabled unchecked
            !isSelected &&
              isDisabled &&
              DEFAULT_STYLES.checkbox__visualUncheckedDisabled,
            // Disabled checked
            isSelected &&
              isDisabled &&
              DEFAULT_STYLES.checkbox__visualCheckedDisabled
          )}
        >
          {isSelected && (
            <svg
              className="w-2 h-2 text-white flex-shrink-0"
              fill="none"
              viewBox="0 0 8 8"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.5 1L3 4.5L1.5 3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>

        {/* Label */}
        {children && (
          <span
            className={cn(
              DEFAULT_STYLES.checkbox__label,
              isSelected &&
                !isDisabled &&
                DEFAULT_STYLES.checkbox__labelChecked,
              isDisabled && DEFAULT_STYLES.checkbox__labelDisabled,
              labelClassName
            )}
          >
            {children}
          </span>
        )}
      </label>

      {/* Single feedback container - always rendered to prevent layout shift */}
      <p
        id={`${checkboxId}-error`}
        className={cn(
          DEFAULT_STYLES.field__message,
          hasError
            ? 'text-errorTextColor'
            : showHelperText
              ? 'text-helperTextColor'
              : 'invisible h-4',
          hintClassName
        )}
        role={hasError ? 'alert' : undefined}
      >
        {hasError ? error : showHelperText ? helperText : ''}
      </p>
    </div>
  );
};

CheckBox.displayName = 'CheckBox';
